import React, { useState } from 'react';
import { useWizard } from '../../context/WizardContext';
import { ArrowLeft, ArrowRight, CheckCircle, Info, BookOpen, MessageSquare, Search, Heart, Users, FileText, Layers } from 'lucide-react';

const B2_QualitativePlanning = () => {
  const { dataB, updateDataRaw, nextStep, prevStep, logDecision } = useWizard();
  const [selectedMethods, setSelectedMethods] = useState(dataB?.qualitativeMethods || {
    thematicAnalysis: false,
    contentAnalysis: false,
    sentimentAnalysis: false,
    narrativeAnalysis: false,
    discourseAnalysis: false,
    groundedTheory: false,
    phenomenologicalAnalysis: false
  });

  const analysisApproach = dataB?.analysisApproach;
  const isQualOnly = analysisApproach === 'qualitative';

  const methods = [
    {
      id: 'thematicAnalysis',
      icon: Layers,
      title: 'Thematic Analysis',
      description: 'Identify patterns and themes across qualitative data',
      dataNeeded: 'Open-ended text responses (long text, paragraph answers)',
      process: 'Code responses → Group codes → Identify themes → Review and refine',
      bestFor: 'Exploring experiences, opinions, and perspectives',
      inWizardF: true
    },
    {
      id: 'contentAnalysis',
      icon: FileText,
      title: 'Content Analysis',
      description: 'Systematically categorize and count themes in text',
      dataNeeded: 'Open-ended text responses, documents, transcripts',
      process: 'Define categories → Code text → Count frequencies → Analyze patterns',
      bestFor: 'Quantifying qualitative content, comparing across groups',
      inWizardF: true
    },
    {
      id: 'sentimentAnalysis',
      icon: Heart,
      title: 'Sentiment Analysis',
      description: 'Analyze emotional tone and polarity of text',
      dataNeeded: 'Open-ended text responses (any length)',
      process: 'Process text → Classify sentiment (positive/negative/neutral) → Generate scores',
      bestFor: 'Understanding attitudes, feelings, and opinions',
      inWizardF: true
    },
    {
      id: 'narrativeAnalysis',
      icon: BookOpen,
      title: 'Narrative Analysis',
      description: 'Analyze stories and personal accounts for meaning',
      dataNeeded: 'Extended narrative responses, interview transcripts',
      process: 'Collect stories → Analyze structure → Identify meaning → Interpret context',
      bestFor: 'Understanding life experiences, identity, meaning-making',
      inWizardF: true
    },
    {
      id: 'discourseAnalysis',
      icon: MessageSquare,
      title: 'Discourse Analysis',
      description: 'Examine language use and social meaning in context',
      dataNeeded: 'Text with social/cultural context, conversations, documents',
      process: 'Select texts → Analyze language patterns → Interpret social meaning',
      bestFor: 'Understanding power relations, social constructions, ideology',
      inWizardF: true
    },
    {
      id: 'groundedTheory',
      icon: Search,
      title: 'Grounded Theory',
      description: 'Develop theory from systematic data analysis',
      dataNeeded: 'Rich qualitative data from interviews or observations',
      process: 'Open coding → Axial coding → Selective coding → Theory development',
      bestFor: 'Generating new theories, explaining processes',
      inWizardF: true
    },
    {
      id: 'phenomenologicalAnalysis',
      icon: Users,
      title: 'Phenomenological Analysis',
      description: 'Explore lived experiences and their essence',
      dataNeeded: 'In-depth interview responses about experiences',
      process: 'Bracket assumptions → Identify themes → Describe essence',
      bestFor: 'Understanding the essence of lived experiences',
      inWizardF: true
    }
  ];

  const toggleMethod = (methodId) => {
    setSelectedMethods(prev => ({
      ...prev,
      [methodId]: !prev[methodId]
    }));
  };

  const getSelectedCount = () => {
    return Object.values(selectedMethods).filter(Boolean).length;
  };

  const handleNext = () => {
    updateDataRaw({
      'wizardB.qualitativeMethods': selectedMethods
    });

    const selectedNames = methods
      .filter(m => selectedMethods[m.id])
      .map(m => m.title)
      .join(', ');

    logDecision(
      'Qualitative Methods',
      selectedNames || 'None selected',
      `Selected ${getSelectedCount()} qualitative analysis method(s)`
    );

    nextStep();
  };

  const canSkip = !isQualOnly;
  const hasSelection = getSelectedCount() > 0;

  return (
    <div className="wizard-step">
      <h2>Step 3: Qualitative Analysis Methods</h2>
      <p className="step-description">
        {isQualOnly 
          ? 'Select the qualitative analysis methods you plan to use. Choose at least one.'
          : 'Select any qualitative analysis methods you want to include. You can skip this if using quantitative only.'
        }
      </p>

      <div style={{
        background: '#f5f3ff',
        border: '1px solid #c4b5fd',
        borderRadius: '8px',
        padding: '1rem 1.25rem',
        marginBottom: '1.5rem',
        display: 'flex',
        alignItems: 'flex-start',
        gap: '0.75rem'
      }}>
        <Info size={20} color="#7c3aed" style={{ flexShrink: 0, marginTop: '2px' }} />
        <div style={{ fontSize: '0.9rem', color: '#5b21b6' }}>
          <strong>Data Collection Tip:</strong> Qualitative methods require open-ended questions in your survey. 
          All methods below are available in Wizard F's analytics panel for automatic analysis.
        </div>
      </div>

      <div style={{ marginBottom: '1rem' }}>
        <span style={{
          background: '#7c3aed',
          color: 'white',
          padding: '4px 12px',
          borderRadius: '16px',
          fontSize: '0.85rem'
        }}>
          {getSelectedCount()} method{getSelectedCount() !== 1 ? 's' : ''} selected
        </span>
      </div>

      <div style={{
        display: 'grid',
        gap: '1rem',
        marginBottom: '2rem'
      }}>
        {methods.map((method) => {
          const Icon = method.icon;
          const isSelected = selectedMethods[method.id];

          return (
            <div
              key={method.id}
              onClick={() => toggleMethod(method.id)}
              style={{
                background: isSelected ? '#f5f3ff' : 'white',
                border: `2px solid ${isSelected ? '#7c3aed' : '#e2e8f0'}`,
                borderRadius: '12px',
                padding: '1.25rem',
                cursor: 'pointer',
                transition: 'all 0.2s ease'
              }}
            >
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '1rem' }}>
                <div style={{
                  width: '44px',
                  height: '44px',
                  borderRadius: '10px',
                  background: isSelected ? '#7c3aed' : '#f5f3ff',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0
                }}>
                  <Icon size={22} color={isSelected ? 'white' : '#7c3aed'} />
                </div>

                <div style={{ flex: 1 }}>
                  <div style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: '0.5rem',
                    marginBottom: '0.25rem'
                  }}>
                    <h4 style={{ margin: 0, color: '#1e293b' }}>{method.title}</h4>
                    {method.inWizardF && (
                      <span style={{
                        background: '#fef3c7',
                        color: '#92400e',
                        padding: '2px 8px',
                        borderRadius: '4px',
                        fontSize: '0.7rem',
                        fontWeight: 600
                      }}>
                        ★ In Wizard F
                      </span>
                    )}
                    {isSelected && <CheckCircle size={18} color="#7c3aed" />}
                  </div>
                  
                  <p style={{ margin: '0 0 0.5rem 0', color: '#475569', fontSize: '0.9rem' }}>
                    {method.description}
                  </p>

                  <div style={{ 
                    display: 'grid',
                    gridTemplateColumns: 'auto 1fr',
                    gap: '0.25rem 0.75rem',
                    fontSize: '0.85rem'
                  }}>
                    <span style={{ color: '#64748b', fontWeight: 500 }}>Data needed:</span>
                    <span style={{ color: '#475569' }}>{method.dataNeeded}</span>
                    <span style={{ color: '#64748b', fontWeight: 500 }}>Best for:</span>
                    <span style={{ color: '#475569' }}>{method.bestFor}</span>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <div className="nav-buttons">
        <button className="btn btn-secondary" onClick={prevStep}>
          <ArrowLeft size={18} />
          Back
        </button>
        <button 
          className="btn btn-primary" 
          onClick={handleNext}
          disabled={isQualOnly && !hasSelection}
        >
          {canSkip && !hasSelection ? 'Skip to Quantitative' : 'Next Step'}
          <ArrowRight size={18} />
        </button>
      </div>
    </div>
  );
};

export default B2_QualitativePlanning;
